import { TbFileText, TbGavel, TbHomeSearch, TbLock } from 'react-icons/tb';

// Guias práticos. Cada seção tem blocos:
//   p      → parágrafo
//   list   → lista com marcadores
//   cards  → quadros lado a lado ({ title, text })
//   steps  → passos numerados ({ strong, text })
// Texto informativo: revisado pelos advogados antes de publicar.

export const GUIAS = [
  {
    slug: 'intimado-para-depor',
    icon: TbFileText,
    cardTitle: 'Fui intimado para depor',
    cardText: 'Se é obrigatório ir, o que levar e quais são os seus direitos na delegacia.',
    title: 'Fui intimado para depor na delegacia: o que fazer',
    subtitle: 'Se é obrigatório ir, o que levar e quais são os seus direitos durante o depoimento.',
    description:
      'Recebeu intimação para depor na delegacia? Veja a diferença entre testemunha e investigado, seus direitos no depoimento e o que levar.',
    readingTime: 4,
    updated: '2026-10-27',
    summary: [
      'Veja na intimação se você foi chamado como testemunha ou como investigado: os direitos e deveres mudam.',
      'O investigado tem direito ao silêncio, e o silêncio não pode ser usado contra ele.',
      'Você pode ir acompanhado de advogado, que tem direito de assistir ao depoimento.'
    ],
    sections: [
      {
        title: 'Testemunha ou investigado?',
        blocks: [
          {
            type: 'p',
            text: 'A intimação costuma indicar em que condição a pessoa está sendo chamada. Essa diferença importa:'
          },
          {
            type: 'cards',
            items: [
              {
                title: 'Testemunha',
                text: 'Em regra, deve comparecer e tem o compromisso de dizer a verdade sobre o que sabe, sob pena de responder por falso testemunho.'
              },
              {
                title: 'Investigado',
                text: 'Pode permanecer em silêncio e não é obrigado a produzir prova contra si. O STF proibiu a condução coercitiva de investigados para interrogatório (ADPFs 395 e 444).'
              }
            ]
          },
          {
            type: 'p',
            text: 'Se a intimação não deixa claro, ou se você acha que pode passar de testemunha a investigado, fale com um advogado antes da data. Mesmo quando o comparecimento não é obrigatório, ignorar a intimação raramente é a melhor estratégia.'
          }
        ]
      },
      {
        title: 'Seus direitos no depoimento',
        blocks: [
          {
            type: 'list',
            items: [
              'Ser acompanhado por advogado, que pode assistir ao depoimento e apresentar requerimentos (Estatuto da Advocacia, art. 7º, XXI).',
              'Permanecer em silêncio, se for investigado (Constituição, art. 5º, LXIII).',
              'Ler o termo do depoimento antes de assinar e pedir a correção do que não corresponder ao que você disse.',
              'Que o seu advogado tenha acesso às provas já documentadas no inquérito (Súmula Vinculante 14 do STF).'
            ]
          }
        ]
      },
      {
        title: 'O que levar',
        blocks: [
          {
            type: 'list',
            items: [
              'A intimação.',
              'Documento oficial com foto.',
              'Documentos ligados aos fatos, apenas se o advogado orientar a apresentá-los.'
            ]
          }
        ]
      },
      {
        title: 'Antes da data',
        blocks: [
          {
            type: 'steps',
            items: [
              { strong: 'Não deixe para a véspera.', text: 'Fale com um advogado assim que receber a intimação, para entender do que se trata.' },
              { strong: 'Não converse sobre os fatos', text: 'com outras pessoas envolvidas, nem por mensagens.' },
              { strong: 'Se não puder comparecer,', text: 'avise a delegacia com antecedência e peça nova data, de preferência por meio do advogado.' }
            ]
          }
        ]
      }
    ]
  },
  {
    slug: 'preso-em-flagrante',
    icon: TbLock,
    cardTitle: 'Um familiar foi preso em flagrante',
    cardText: 'O que acontece nas primeiras 24 horas e como a família pode agir.',
    title: 'Um familiar foi preso em flagrante: o que fazer',
    subtitle: 'O que acontece nas primeiras 24 horas, quais são os direitos de quem foi preso e como a família pode agir.',
    description:
      'Um familiar foi preso em flagrante? Entenda o que acontece nas primeiras 24 horas, a audiência de custódia, os direitos do preso e como a família pode agir.',
    readingTime: 4,
    updated: '2026-10-27',
    summary: [
      'Em até 24 horas, a pessoa presa deve ser levada a um juiz na audiência de custódia.',
      'Ela tem direito a ficar em silêncio e a ser assistida por advogado e pela família.',
      'Procurar um advogado antes da audiência de custódia faz diferença no pedido de liberdade.'
    ],
    sections: [
      {
        title: 'O que acontece logo depois da prisão',
        blocks: [
          {
            type: 'p',
            text: 'A pessoa é levada à delegacia. O delegado ouve quem fez a prisão, as testemunhas e o próprio preso, e registra tudo no auto de prisão em flagrante.'
          },
          {
            type: 'p',
            text: 'A prisão e o local onde a pessoa está devem ser comunicados imediatamente ao juiz e à família ou a alguém indicado por ela (Constituição, art. 5º, LXII). Em até 24 horas, o auto é enviado ao juiz e o preso recebe a nota de culpa, com o motivo da prisão e o nome de quem a realizou (CPP, art. 306).'
          }
        ]
      },
      {
        title: 'A audiência de custódia',
        blocks: [
          {
            type: 'p',
            text: 'Em até 24 horas após a prisão, a pessoa deve ser apresentada a um juiz, com a presença do Ministério Público e da defesa (CPP, art. 310). Nessa audiência ainda não se discute se ela é culpada. O juiz decide uma de três coisas:'
          },
          {
            type: 'cards',
            items: [
              { title: 'Relaxar a prisão', text: 'Quando a prisão foi ilegal.' },
              { title: 'Liberdade provisória', text: 'Com ou sem fiança, podendo impor medidas cautelares.' },
              { title: 'Prisão preventiva', text: 'Quando entende que os requisitos legais estão presentes.' }
            ]
          }
        ]
      },
      {
        title: 'Direitos de quem foi preso',
        blocks: [
          {
            type: 'list',
            items: [
              'Permanecer em silêncio, sem que isso seja usado contra ele (Constituição, art. 5º, LXIII).',
              'Ser assistido por advogado e pela família (art. 5º, LXIII).',
              'Saber quem são os responsáveis pela prisão e pelo interrogatório (art. 5º, LXIV).',
              'Ter respeitada a integridade física e moral (art. 5º, XLIX).'
            ]
          }
        ]
      },
      {
        title: 'O que a família pode fazer agora',
        blocks: [
          {
            type: 'steps',
            items: [
              { strong: 'Descubra para qual delegacia a pessoa foi levada.', text: 'Anote o horário e o nome de quem passou a informação.' },
              { strong: 'Procure um advogado o quanto antes,', text: 'de preferência antes da audiência de custódia.' },
              {
                strong: 'Reúna documentos',
                text: 'que mostrem endereço fixo e trabalho: documento de identidade, comprovante de residência, carteira de trabalho ou declaração do empregador.'
              },
              { strong: 'Não comente o caso', text: 'em redes sociais, mensagens ou ligações.' }
            ]
          }
        ]
      }
    ]
  },
  {
    slug: 'busca-e-apreensao',
    icon: TbHomeSearch,
    cardTitle: 'Busca e apreensão em casa ou na empresa',
    cardText: 'O que a polícia pode e não pode fazer ao cumprir o mandado.',
    title: 'Busca e apreensão em casa ou na empresa: o que a polícia pode fazer',
    subtitle: 'Quando a entrada é permitida, como deve ser cumprido o mandado e como agir durante a diligência.',
    description:
      'Mandado de busca e apreensão em casa ou na empresa: quando a polícia pode entrar, o horário permitido, seus direitos durante a diligência e como agir.',
    readingTime: 4,
    updated: '2026-10-27',
    summary: [
      'Fora do flagrante, a polícia só pode entrar na casa com mandado judicial e durante o dia.',
      'Você tem direito de ler o mandado e de acompanhar a busca.',
      'Não resista à diligência e chame um advogado imediatamente.'
    ],
    sections: [
      {
        title: 'Quando a polícia pode entrar',
        blocks: [
          {
            type: 'p',
            text: 'A casa é protegida pela Constituição. Sem o consentimento do morador, a entrada só é permitida em caso de flagrante delito, desastre ou para prestar socorro, ou, durante o dia, por ordem judicial (art. 5º, XI).'
          },
          {
            type: 'p',
            text: 'O escritório ou a sala de uma empresa que não é aberta ao público recebe a mesma proteção. E cumprir mandado de busca domiciliar depois das 21h ou antes das 5h é proibido pela Lei de Abuso de Autoridade (Lei 13.869/2019, art. 22, § 1º, III).'
          }
        ]
      },
      {
        title: 'O que o mandado deve conter',
        blocks: [
          {
            type: 'p',
            text: 'O mandado é expedido por um juiz e deve indicar, o mais precisamente possível, o local da busca, o motivo e o que se procura (CPP, art. 243).'
          }
        ]
      },
      {
        title: 'Seus direitos durante a busca',
        blocks: [
          {
            type: 'list',
            items: [
              'Ver e ler o mandado antes do início da diligência (CPP, art. 245).',
              'Acompanhar a busca, pessoalmente ou por alguém de confiança.',
              'Que, ao final, seja lavrado auto com a relação do que foi apreendido. Peça uma cópia.',
              'Não produzir prova contra si: antes de fornecer senhas de celulares e computadores, fale com o seu advogado.'
            ]
          }
        ]
      },
      {
        title: 'Como agir',
        blocks: [
          {
            type: 'steps',
            items: [
              { strong: 'Mantenha a calma e não resista.', text: 'Resistir ou atrapalhar a diligência pode configurar outro crime.' },
              { strong: 'Ligue para um advogado logo no início.', text: 'Ele pode orientar por telefone e, quando possível, acompanhar a busca.' },
              { strong: 'Confira o que é levado', text: 'e anote o nome dos policiais e os horários de início e fim.' },
              {
                strong: 'Na empresa, oriente a equipe',
                text: 'a não atrapalhar a diligência e a não prestar declarações sobre os fatos antes de falar com o advogado.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    slug: 'citacao-processo-criminal',
    icon: TbGavel,
    cardTitle: 'Recebi uma citação em processo criminal',
    cardText: 'O prazo para a defesa e por que não deixar para a última hora.',
    title: 'Recebi uma citação em processo criminal: o que fazer',
    subtitle: 'O que significa ser citado, qual é o prazo para a defesa e o que ela pode conter.',
    description:
      'Recebeu citação em processo criminal? Entenda o prazo de 10 dias para a resposta à acusação, o que a defesa pode apresentar e o que fazer agora.',
    readingTime: 4,
    updated: '2026-10-27',
    summary: [
      'A citação informa que o juiz recebeu a denúncia do Ministério Público: o processo começou.',
      'O prazo para a resposta à acusação é de 10 dias, contados da citação.',
      'É nessa resposta que a defesa indica testemunhas e pode pedir a absolvição sumária.'
    ],
    sections: [
      {
        title: 'O que é a citação',
        blocks: [
          {
            type: 'p',
            text: 'A citação comunica oficialmente ao acusado que existe uma ação penal contra ele e entrega a cópia da denúncia. A partir dela, ele passa a ser réu e precisa apresentar defesa por meio de advogado.'
          }
        ]
      },
      {
        title: 'O prazo de 10 dias',
        blocks: [
          {
            type: 'p',
            text: 'A resposta à acusação deve ser apresentada em 10 dias (CPP, art. 396). Se o acusado não constituir advogado nesse prazo, o juiz nomeia um defensor para fazê-la (CPP, art. 396-A, § 2º).'
          },
          {
            type: 'p',
            text: 'Deixar para a última hora reduz o tempo para estudar o processo, reunir documentos e localizar testemunhas.'
          }
        ]
      },
      {
        title: 'O que a defesa pode apresentar',
        blocks: [
          {
            type: 'cards',
            items: [
              { title: 'Teses e documentos', text: 'Tudo o que interessa à defesa, inclusive questões que podem encerrar o processo logo no início.' },
              { title: 'Testemunhas', text: 'É o momento de indicar as testemunhas de defesa, até 8 no procedimento comum ordinário.' },
              { title: 'Absolvição sumária', text: 'Nas hipóteses previstas em lei, o juiz pode absolver já nessa fase (CPP, art. 397).' }
            ]
          }
        ]
      },
      {
        title: 'Acordos e benefícios',
        blocks: [
          {
            type: 'p',
            text: 'Dependendo do crime e da situação do acusado, a lei pode admitir alternativas como a suspensão condicional do processo (Lei 9.099/1995, art. 89). Avaliar se elas cabem no caso, e se são vantajosas, é uma decisão a tomar com o advogado.'
          }
        ]
      },
      {
        title: 'O que fazer agora',
        blocks: [
          {
            type: 'steps',
            items: [
              { strong: 'Anote a data em que recebeu a citação.', text: 'O prazo é contado a partir dela.' },
              { strong: 'Procure um advogado logo', text: 'e leve a citação e a cópia da denúncia.' },
              { strong: 'Separe documentos e contatos', text: 'de pessoas que possam ser testemunhas.' },
              { strong: 'Mantenha seu endereço atualizado no processo.', text: 'Mudar sem informar pode trazer consequências para a defesa.' }
            ]
          }
        ]
      }
    ]
  }
];

export const findGuia = (slug) => GUIAS.find((g) => g.slug === slug);

// "outubro de 2026"
export const formatUpdated = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
